import { useLayoutEffect, useRef, useState } from 'react';
import {
  isSendShortcut,
  type SendShortcutEvent,
} from '../composer/sendShortcut';

// React keyboard events satisfy this shape.
type ComposerKeyEvent = SendShortcutEvent & { preventDefault: () => void };

type UseChatComposerOptions = {
  /** Resolves to whether the text was sent and the draft can be cleared. */
  send: (text: string) => Promise<boolean> | boolean;
  disabled?: boolean;
};

export const useChatComposer = ({
  send,
  disabled = false,
}: UseChatComposerOptions) => {
  const [text, setText] = useState('');
  const activeRef = useRef(true);
  const trimmedText = text.trim();

  useLayoutEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const submit = async () => {
    if (!trimmedText || disabled) {
      return;
    }

    const sent = await send(trimmedText);
    if (sent && activeRef.current) {
      // The field may stay editable while sending: keep a newer draft.
      setText(current => (current.trim() === trimmedText ? '' : current));
    }
  };

  const handleKeyDown = (event: ComposerKeyEvent) => {
    if (isSendShortcut(event)) {
      event.preventDefault();
      void submit();
    }
  };

  return {
    text,
    setText,
    hasText: trimmedText.length > 0,
    submit,
    handleKeyDown,
  };
};
